import React, { FC } from 'react'
import { Hidden, CardContent } from '@mui/material'
import { Events } from '../../FSM/shared/constants'
import { CoverageDetailedInfo, Typography } from '@dg-shared'
import { useTranslate } from '../../hooks/shared'
import BtnEdit from '@dg-shared/BtnEdit'
import useContentStyles from '../../hooks/shared/useContentStyles'
import { FSMService } from '../../FSM'
import {
  getScheduledChangeStart,
  hasPolicyCoverageRemaining,
  isCurrentActivePolicy,
} from '@dg-util'
import { format, addDays, startOfDay } from 'date-fns'
import { FORMAT_DAY_MONTH_YEAR_DOT } from '../../constants'
import Icon from '@mui/material/Icon'
import { ProductName } from '../../types/interface/quote.interface'
import PolicyReviewVehicleDetails from '@dg-shared/PolicyReviewVehicleDetails'
import PolicyReviewTravelDetails from '@dg-shared/PolicyReviewTravelDetails'
import PolicyReviewContentDetails from '@dg-shared/PolicyReviewContentDetails'
import { PolicyReviewCardProps } from '../../types'

const PolicyReviewCard: FC<PolicyReviewCardProps> = ({ policy, productProps, children }) => {
  const { classes } = useContentStyles()
  const { t } = useTranslate()

  const coverageInfo = (
    <CoverageDetailedInfo
      send={FSMService.flowService.send}
      state={FSMService.flowService.getSnapshot().value}
      coverageType={policy.CarPackage}
      translationCondition={policy.SuperCoverage ? 'isSuper' : 'isNotSuper'}
    />
  )

  const policyReviewCardDetailsPerProduct: Record<ProductName, React.FC<PolicyReviewCardProps>> = {
    [ProductName.VEHICLE]: PolicyReviewVehicleDetails,
    [ProductName.TRAVEL]: PolicyReviewTravelDetails,
    [ProductName.CONTENT]: PolicyReviewContentDetails,
  }

  const PolicyDetails = policyReviewCardDetailsPerProduct[policy.productName]

  // The earliest date the change form will offer — `definePolicyNotChangeDate(1)`, i.e.
  // tomorrow. i2i rejects a change dated outside the loaded version's period, so if this
  // version's cover ends today there is no date the customer could pick that would be
  // accepted, and offering the button leads to a submit that fails on the generic error
  // page. Same inclusive-last-day rule as everywhere else — compared against the change
  // date rather than `now` (DFE-4112).
  const coversEarliestChangeDate = hasPolicyCoverageRemaining(
    policy,
    startOfDay(addDays(new Date(), 1))
  )

  // Only asked when the policy cannot be changed, to tell the two reasons apart: a change
  // already registered today (self-resolves when that version takes over) versus cover
  // that has simply run out.
  const scheduledChangeStart = coversEarliestChangeDate ? null : getScheduledChangeStart(policy)

  // Don't allow to change policy which is inactive or invalid
  const isEditable = isCurrentActivePolicy(policy) && coversEarliestChangeDate

  return productProps && policy ? (
    <CardContent className={classes.twoCol}>
      <section
        className={classes.colLeft}
        data-testid={'quote-info-block'}
        style={{ justifyContent: 'start' }}
      >
        {children}
        <>
          <PolicyDetails policy={policy} productProps={productProps} />
          {isEditable ? (
            <BtnEdit
              id='change-offer-button'
              testId='change-offer-button'
              handleClick={() => FSMService.flowService.send({ type: Events.CHANGE })}
              text={t(`editButtonLabel`)}
            />
          ) : (
            <div className={classes.inactivePolicyText}>
              <Icon fontSize='small' className={classes.infoIconStyle}>
                info_outlined
              </Icon>
              <Typography color={'textSecondary'}>
                {scheduledChangeStart
                  ? t('changeAlreadyScheduled', {
                      date: format(scheduledChangeStart, FORMAT_DAY_MONTH_YEAR_DOT),
                    })
                  : t('inactivePolicy')}
              </Typography>
            </div>
          )}
        </>
      </section>
      <Hidden mdDown>
        <div className={classes.colRight}>{coverageInfo}</div>
      </Hidden>
    </CardContent>
  ) : null
}

export default PolicyReviewCard
